import { Calendar, Clock, MapPin, Utensils } from 'lucide-react'
import { cn } from '../lib/utils'
import { useVersion } from '../contexts/VersionContext'

type ItineraryEvent = {
  id: number
  title: string
  date: string
  time: string
  /** When set, shown struck through before `time` (schedule update). */
  timePrevious?: string
  location: string
  address: string
  description: string
  attire: string
  additionalInfo?: string
}

const events: ItineraryEvent[] = [
  {
    id: 1,
    title: 'Calenda',
    date: 'Friday, April 3rd',
    time: '4:00 PM',
    timePrevious: '6:00 PM',
    location: 'Templo de Santo Domingo de Guzmán',
    address: 'Macedonio Alcalá s/n, Centro, 68000 Oaxaca de Juárez, Oax., Mexico',
    description: 'Join us for a traditional Oaxacan procession celebrating our union. Experience the vibrant culture and music of Oaxaca.',
    attire: 'Cocktail Casual',
    additionalInfo: 'We will be dancing and walking along the streets of Oaxaca, so dress classy and comfortable. Come ready to celebrate!'
  },
  {
    id: 2,
    title: 'Welcome Drinks',
    date: 'Friday, April 3rd',
    time: '5:30 PM - 8:30 PM',
    timePrevious: '7:00 PM',
    location: 'Zandunga Sabor Istmeño',
    address: 'García Vigil 512, Centro, 68000 Oaxaca de Juárez, Oax., Mexico',
    description: 'Join us for welcome drinks and some authentic Oaxacan bites. Meet our beloved guests and family!',
    attire: 'Cocktail Casual'
  },
  {
    id: 3,
    title: 'Wedding Ceremony',
    date: 'Saturday, April 4th',
    time: '5:00 PM',
    location: 'Casa Don Luis Jardín Boutique',
    address: 'Camino Real #1201, San Agustín de las Juntas, Oaxaca, Mexico',
    description: 'Our wedding ceremony in the beautiful gardens of Casa Don Luis.',
    attire: 'Garden Party Formal',
    additionalInfo: 'Please arrive by 4:45 PM. Transportation provided from select hotels.'
  },
  {
    id: 4,
    title: 'Cocktail Hour',
    date: 'Saturday, April 4th',
    time: '6:00 PM',
    location: 'Casa Don Luis Jardín Boutique',
    address: 'Camino Real #1201, San Agustín de las Juntas, Oaxaca, Mexico',
    description: 'Cocktails and appetizers in the gardens following the ceremony.',
    attire: 'Garden Party Formal',
    additionalInfo: 'Enjoy signature cocktails and hors d\'oeuvres while we take photos.'
  },
  {
    id: 5,
    title: 'Reception',
    date: 'Saturday, April 4th',
    time: '7:00 PM - Late AF',
    location: 'Casa Don Luis Jardín Boutique',
    address: 'Camino Real #1201, San Agustín de las Juntas, Oaxaca, Mexico',
    description: 'Dinner, dancing, and celebration under the stars in the beautiful gardens.',
    attire: 'Garden Party Formal',
    additionalInfo: 'Dinner will be served followed by dancing and celebration into the night.'
  }
]

export function Itinerary() {
  const { isV2 } = useVersion()
  
  // Only render the itinerary in V2
  if (!isV2) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-serif text-primary-900 mb-4">
            Coming Soon
          </h1>
          <p className="text-lg text-neutral-600">
            The detailed itinerary will be available soon!
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-serif text-primary-900 mb-4">
              Wedding Itinerary
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Join us for a long weekend of celebration in beautiful Oaxaca, Mexico. 
              Here's what we have planned for our special days together.
            </p>
            <p className="text-sm text-neutral-500 max-w-xl mx-auto mt-4">
              Friday's Calenda and Welcome Drinks have revised times — look for the “Schedule updated” note and crossed-out earlier times.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {events.map((event) => (
              <div
                key={event.id}
                className={cn(
                  'card p-8',
                  event.timePrevious &&
                    'bg-primary-50/35 shadow-sm ring-1 ring-inset ring-primary-200/70'
                )}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-primary-600" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                      <h3 className="text-2xl font-serif text-primary-900">
                        {event.title}
                      </h3>
                      {event.timePrevious ? (
                        <span className="shrink-0 inline-flex items-center rounded-full border border-primary-200/90 bg-white/80 px-2.5 py-1 text-xs font-medium text-primary-800">
                          Schedule updated
                        </span>
                      ) : null}
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-neutral-600">
                        <Calendar className="h-4 w-4" />
                        <span>{event.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-neutral-600 flex-wrap">
                        <Clock className="h-4 w-4 shrink-0" />
                        <span>
                          {event.timePrevious ? (
                            <>
                              <span className="line-through text-neutral-400 mr-2" title="Previous time">
                                {event.timePrevious}
                              </span>
                              <span className="font-medium text-neutral-800">{event.time}</span>
                            </>
                          ) : (
                            event.time
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-neutral-600">
                        <MapPin className="h-4 w-4" />
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-neutral-600">
                        <Utensils className="h-4 w-4" />
                        <span>{event.attire}</span>
                      </div>
                    </div>
                    
                    <p className="text-neutral-600 mb-4">
                      {event.description}
                    </p>
                    
                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="font-semibold text-neutral-900 mb-2">Additional Information</h4>
                      <p className="text-sm text-neutral-600 mb-2">
                        <strong>Address:</strong> {event.address}
                      </p>
                      {
                        event.additionalInfo && (<p className="text-sm text-neutral-600">
                          <strong>Note:</strong> {event.additionalInfo}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="card p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-serif text-primary-900 mb-4">
                Important Notes
              </h3>
              <div className="space-y-3 text-left">
                <p className="text-neutral-600">
                  <strong>Transportation:</strong> We'll provide transportation for the wedding day. 
                  Pick-up and drop-off details will be shared closer to the date.
                </p>
                <p className="text-neutral-600 mb-4">
                  <strong>Weather:</strong> April is a beautiful month in Oaxaca with temperatures around 75-85°F (24-29°C).
                  Perfect weather for celebrating!
                </p>
                <p className="text-neutral-600 text-center">
                  For any questions about the itinerary, please contact us at{' '}
                  <a href="mailto:naetzy@gmail.com" className="text-primary-600 hover:text-primary-700">
                    naetzy@gmail.com
                  </a>
                  {' '}or{' '}
                  <a href="mailto:eduardo.rios.jr.7@gmail.com" className="text-primary-600 hover:text-primary-700">
                    eduardo.rios.jr.7@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 